import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixBillingV1TaxRegionsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www.wixapis.com': [
      {
        srcPath: '/billing/v1/tax-regions',
        destPath: '/v1/tax-regions',
      },
      {
        srcPath: '/billing/v1/bulk/tax-regions',
        destPath: '/v1/bulk/tax-regions',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/billing/v1/tax-regions',
        destPath: '/v1/tax-regions',
      },
      {
        srcPath: '/billing/v1/bulk/tax-regions',
        destPath: '/v1/bulk/tax-regions',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_billing_tax-regions';

/**
 * Creates a tax region.
 *
 * Tax regions require a tax calculator `appId`. Call List Tax Calculators to retrieve a list of available calculators for a site.
 *
 * Wix uses tax regions to calculate tax.
 * <!-- Subdivisions are supported for the following countries: `AU`, `BR`, `CA`, `FR`, `DE`, `IN`, `IT`, `MX`, `NL`, `ES`, `AE`, `GB`, `US` -->
 */
export function createTaxRegion(payload: object): RequestOptionsFactory<any> {
  function __createTaxRegion({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'taxRegion.createdDate' },
          { path: 'taxRegion.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.billing.v1.tax_region',
      method: 'POST' as any,
      methodFqn: 'wix.billing.v1.TaxRegionsService.CreateTaxRegion',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBillingV1TaxRegionsServiceUrl({
        protoPath: '/v1/tax-regions',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'taxRegion.createdDate' },
              { path: 'taxRegion.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createTaxRegion;
}

/** Retrieves a tax region. */
export function getTaxRegion(payload: object): RequestOptionsFactory<any> {
  function __getTaxRegion({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.billing.v1.tax_region',
      method: 'GET' as any,
      methodFqn: 'wix.billing.v1.TaxRegionsService.GetTaxRegion',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBillingV1TaxRegionsServiceUrl({
        protoPath: '/v1/tax-regions/{taxRegionId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'taxRegion.createdDate' },
              { path: 'taxRegion.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getTaxRegion;
}

/**
 * Updates a tax region.
 *
 * Each time the tax region is updated, `revision` increments by 1.
 * The current `revision` must be passed when updating the tax region.
 * This ensures you're working with the latest tax region and prevents
 * unintended overwrites.
 */
export function updateTaxRegion(payload: object): RequestOptionsFactory<any> {
  function __updateTaxRegion({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'taxRegion.createdDate' },
          { path: 'taxRegion.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.billing.v1.tax_region',
      method: 'PATCH' as any,
      methodFqn: 'wix.billing.v1.TaxRegionsService.UpdateTaxRegion',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBillingV1TaxRegionsServiceUrl({
        protoPath: '/v1/tax-regions/{taxRegion.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'taxRegion.createdDate' },
              { path: 'taxRegion.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateTaxRegion;
}

/**
 * Deletes a tax region.
 *
 * When a tax region is deleted, tax is not calculated and zero tax will be returned for addresses in this region.
 */
export function deleteTaxRegion(payload: object): RequestOptionsFactory<any> {
  function __deleteTaxRegion({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.billing.v1.tax_region',
      method: 'DELETE' as any,
      methodFqn: 'wix.billing.v1.TaxRegionsService.DeleteTaxRegion',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBillingV1TaxRegionsServiceUrl({
        protoPath: '/v1/tax-regions/{taxRegionId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteTaxRegion;
}

/**
 * Creates a query to retrieve a list of tax regions.
 *
 * The `queryTaxRegions()` function builds a query to retrieve a list of tax regions and returns a `TaxRegionsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is typically used to run the query using the `find()` function.
 *
 * You can refine the query by chaining `TaxRegionsQueryBuilder` functions onto the query. `TaxRegionsQueryBuilder` functions enable you to sort, filter, and control the results that `queryTaxRegions()` returns.
 *
 * `queryTaxRegions()` runs with the following `TaxRegionsQueryBuilder` default that you can override:
 * + `ascending("_id")`
 *
 * The functions that are chained to `queryTaxRegions()` are applied in the order they are called. For example, if you apply `ascending("country")` and then `ascending("subdivision")`, the results are sorted first by the `"country"`, and then, if there are multiple results with the same `"country"`, the items are sorted by `"subdivision"`.
 *
 * The following `TaxRegionsQueryBuilder` functions are supported for the `queryTaxRegions()` function. For a full description of the tax region object, see the object returned for the `items` property in `TaxRegionsQueryResult`.
 */
export function queryTaxRegions(payload: object): RequestOptionsFactory<any> {
  function __queryTaxRegions({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.billing.v1.tax_region',
      method: 'POST' as any,
      methodFqn: 'wix.billing.v1.TaxRegionsService.QueryTaxRegions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBillingV1TaxRegionsServiceUrl({
        protoPath: '/v1/tax-regions/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'taxRegions.createdDate' },
              { path: 'taxRegions.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryTaxRegions;
}
